// Aba "Drive" do detalhe do contrato — pastas + versões + contatos externos +
// link público de compartilhamento.
// Wave 3 (Antd) — 2026-06-01. Lógica de upload/state preservada — só visual.

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Input, Button, Card, Table, Tag, Tooltip, Empty, Spin, Typography, Space, Flex,
  Modal, Form, Checkbox, App as AntApp, Alert, DatePicker, Popconfirm, Progress,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SearchOutlined, PlusOutlined, ShareAltOutlined, TeamOutlined,
  DeleteOutlined, EditOutlined, DownloadOutlined, HistoryOutlined,
  CloudUploadOutlined, CopyOutlined, CheckOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import type { Contrato, DriveFolder, DriveFile } from './types';
import { DRIVE_TIPO_ICONE } from './types';
import { fmtData, fmtTamanho, iconeArquivo, getToken } from './utils';

export function Drive(props: { contrato: Contrato; docs: any[]; onAtualizar: () => void }) {
  return <AntApp><DriveInner {...props} /></AntApp>;
}

function DriveInner({ contrato }: { contrato: Contrato; docs: any[]; onAtualizar: () => void }) {
  const { message, modal } = AntApp.useApp();
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [pastaAtiva, setPastaAtiva] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  // Criação de pasta/subpasta via modal. parentId null = pasta raiz.
  const [criarEm, setCriarEm] = useState<{ parentId: string | null; parentNome: string } | null>(null);
  const [novoPastaNome, setNovoPastaNome] = useState('');
  // Pastas colapsadas na árvore (vazio = tudo aberto). Sobrevive ao recarregar.
  const [colapsadas, setColapsadas] = useState<Set<string>>(new Set());
  const [verContatos, setVerContatos] = useState(false);
  const [verCompartilhar, setVerCompartilhar] = useState(false);
  const [verVersoes, setVerVersoes] = useState<DriveFile | null>(null);
  const [dragSobre, setDragSobre] = useState(false);
  const [enviando, setEnviando] = useState<{ atual: number; total: number; nome: string; pct: number } | null>(null);

  async function recarregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_listar_arvore', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setFolders(d.folders || []);
      setFiles(d.files || []);
      if (!pastaAtiva && d.folders?.length) {
        const com = d.folders.find((f: DriveFolder) => f.tipo === 'comercial');
        setPastaAtiva(com?.id || d.folders[0].id);
      }
    } catch (e: any) {
      message.error('Erro ao carregar Drive: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }

  useEffect(() => { recarregar(); /* eslint-disable-next-line */ }, [contrato.id]);

  const pasta = folders.find(f => f.id === pastaAtiva) || null;
  const arquivosPasta = files.filter(f => f.folder_id === pastaAtiva);
  const arquivosFiltrados = busca
    ? arquivosPasta.filter(f => f.nome.toLowerCase().includes(busca.toLowerCase()))
    : arquivosPasta;

  const podeUpload = pasta && pasta.tipo !== 'arts';

  // MULTIPART (2026-07-17): arquivos de 5GB a 15GB — o PUT único do S3 tem
  // teto de 5GB, então acima disso o arquivo sobe fatiado em partes de 100MB
  // (iniciar → URLs presigned em lotes de 10 → PUT das partes coletando o
  // ETag → completar). Em erro, aborta pra não deixar partes órfãs no S3.
  async function uploadMultipartArquivo(file: File, token: string): Promise<DriveFile | null> {
    const PARTE = 100 * 1024 * 1024;
    const post = (body: any) => axios.post(`${API_URL}/comercial/propostas-geradas`,
      body, { headers: { Authorization: token } });
    const unwrap = (r: any) => (r.data?.body ? JSON.parse(r.data.body) : r.data);

    const d0 = unwrap(await post({
      action: 'op_drive_multipart_iniciar',
      contrato_id: contrato.id, folder_id: pastaAtiva,
      filename: file.name, content_type: file.type || 'application/octet-stream',
      tamanho_bytes: file.size }));
    if (!d0.upload_id || !d0.s3_key) {
      message.error(`Erro ao iniciar upload de "${file.name}": ${d0.error || 'sem upload_id'}`);
      return null;
    }
    const totalPartes = Math.ceil(file.size / PARTE);
    const etags: { PartNumber: number; ETag: string }[] = [];
    let enviadoBytes = 0;
    try {
      for (let inicio = 1; inicio <= totalPartes; inicio += 10) {
        const nums: number[] = [];
        for (let n = inicio; n < inicio + 10 && n <= totalPartes; n++) nums.push(n);
        const dU = unwrap(await post({
          action: 'op_drive_multipart_urls',
          contrato_id: contrato.id, s3_key: d0.s3_key, upload_id: d0.upload_id, partes: nums }));
        for (const n of nums) {
          const blob = file.slice((n - 1) * PARTE, Math.min(n * PARTE, file.size));
          const rP = await axios.put(dU.urls[n], blob, {
            onUploadProgress: (ev) => {
              const pct = Math.round(((enviadoBytes + (ev.loaded || 0)) / file.size) * 100);
              setEnviando(s => s ? { ...s, pct } : s);
            },
          });
          const etag = rP.headers?.etag || rP.headers?.ETag;
          if (!etag) throw new Error('S3 não devolveu o ETag da parte (CORS sem ExposeHeaders?)');
          etags.push({ PartNumber: n, ETag: etag });
          enviadoBytes += blob.size;
        }
      }
      const novo = unwrap(await post({
        action: 'op_drive_multipart_completar',
        contrato_id: contrato.id, folder_id: pastaAtiva,
        s3_key: d0.s3_key, upload_id: d0.upload_id, partes: etags,
        nome: file.name, tamanho_bytes: file.size }));
      return novo?.id ? novo : null;
    } catch (e) {
      try {
        await post({ action: 'op_drive_multipart_abortar',
          s3_key: d0.s3_key, upload_id: d0.upload_id });
      } catch { /* melhor esforço */ }
      throw e;
    }
  }

  async function uploadArquivos(arquivos: FileList | File[]) {
    if (!pastaAtiva || !podeUpload) return;
    const lista = Array.from(arquivos);
    if (lista.length === 0) return;
    setEnviando({ atual: 0, total: lista.length, nome: '', pct: 0 });
    const novosFiles: DriveFile[] = [];
    try {
      const token = await getToken();
      for (let i = 0; i < lista.length; i++) {
        const file = lista[i];
        setEnviando({ atual: i + 1, total: lista.length, nome: file.name, pct: 0 });
        // 2026-07-17: limite subido de 5GB pra 15GB. Até 5GB segue no PUT
        // único (teto do S3); de 5GB a 15GB vai por multipart.
        if (file.size > 15 * 1024 * 1024 * 1024) {
          message.warning(`"${file.name}" excede 15GB (limite do Drive). Pulando.`);
          continue;
        }
        if (file.size > 5 * 1024 * 1024 * 1024) {
          const novoMp = await uploadMultipartArquivo(file, token);
          if (novoMp?.id) novosFiles.push(novoMp);
          continue;
        }
        const r1 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_drive_upload_url',
            contrato_id: contrato.id, folder_id: pastaAtiva,
            filename: file.name, content_type: file.type || 'application/octet-stream' },
          { headers: { Authorization: token } });
        const d1 = r1.data?.body ? JSON.parse(r1.data.body) : r1.data;
        if (!d1.upload_url || !d1.s3_key) {
          message.error(`Erro ao obter URL de upload pra "${file.name}".`);
          continue;
        }
        await axios.put(d1.upload_url, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            setEnviando(s => s ? { ...s, pct } : s);
          },
        });
        const r3 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_drive_confirmar_upload',
            contrato_id: contrato.id, folder_id: pastaAtiva,
            s3_key: d1.s3_key, nome: file.name,
            tamanho_bytes: file.size },
          { headers: { Authorization: token } });
        const novo = r3.data?.body ? JSON.parse(r3.data.body) : r3.data;
        if (novo?.id) novosFiles.push(novo);
      }
    } catch (e: any) {
      message.error('Erro no upload: ' + (e.response?.data?.error || e.message));
    } finally {
      setEnviando(null);
      if (novosFiles.length) {
        setFiles(curr => {
          const nomesNovos = new Set(novosFiles.map(n => n.folder_id + '|' + n.nome));
          const filtered = curr.filter(f => !nomesNovos.has(f.folder_id + '|' + f.nome));
          return [...novosFiles, ...filtered];
        });
      }
      await recarregar();
    }
  }

  async function abrirArquivo(f: DriveFile) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_download_url', documento_id: f.id },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      if (d.url) window.open(d.url, '_blank');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function excluirArquivo(f: DriveFile) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_excluir_arquivo', documento_id: f.id },
        { headers: { Authorization: token } });
      message.success('Arquivo excluído.');
      recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function criarPasta() {
    if (!novoPastaNome.trim() || !criarEm) return;
    const parentId = criarEm.parentId;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_criar_pasta',
          contrato_id: contrato.id, nome: novoPastaNome.trim(),
          parent_id: parentId },
        { headers: { Authorization: token } });
      setNovoPastaNome(''); setCriarEm(null);
      // Garante que o pai fica expandido pra mostrar a nova subpasta
      if (parentId) setColapsadas(s => { const n = new Set(s); n.delete(parentId); return n; });
      recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  function toggleColapso(id: string) {
    setColapsadas(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  // Subpasta permitida em qualquer pasta menos ARTs (gerenciada pela aba) e
  // Upload do Cliente (fluxo do cliente externo é só a raiz).
  const podeSubpasta = (p: DriveFolder) => p.tipo !== 'arts' && p.tipo !== 'upload_cliente';
  // Renomear/excluir liberado em tudo menos as 3 pastas essenciais do sistema.
  const TIPOS_PASTA_PROTEGIDOS = ['comercial', 'arts', 'upload_cliente'];
  const podeRenomearExcluir = (p: DriveFolder) => !TIPOS_PASTA_PROTEGIDOS.includes(p.tipo);
  const filhosDe = (pid: string | null) =>
    folders.filter(f => (f.parent_id || null) === pid)
      .sort((a, b) => (a.ordem - b.ordem) || a.nome.localeCompare(b.nome));

  async function renomearPasta(f: DriveFolder) {
    const novo = window.prompt('Novo nome:', f.nome);
    if (!novo || novo.trim() === f.nome) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_renomear_pasta', id: f.id, nome: novo.trim() },
        { headers: { Authorization: token } });
      message.success('Renomeada.');
      recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function excluirPasta(f: DriveFolder) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_excluir_pasta', id: f.id },
        { headers: { Authorization: token } });
      message.success('Pasta excluída.');
      if (pastaAtiva === f.id) setPastaAtiva(null);
      recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  function pedirExcluirArquivo(f: DriveFile) {
    modal.confirm({
      title: `Excluir "${f.nome}"?`,
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Excluir', okButtonProps: { danger: true },
      onOk: () => excluirArquivo(f),
    });
  }

  if (loading && folders.length === 0) {
    return <Flex justify="center" align="center" style={{ minHeight: 200 }}><Spin tip="Carregando Drive..." size="large" /></Flex>;
  }

  // Linha de pasta na árvore — recursiva (N níveis), com expand/collapse.
  function renderNo(f: DriveFolder, nivel: number) {
    const filhos = filhosDe(f.id);
    const temFilhos = filhos.length > 0;
    const aberta = !colapsadas.has(f.id);
    const ativa = pastaAtiva === f.id;
    const arq = files.filter(x => x.folder_id === f.id).length;
    return (
      <div key={f.id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4,
          padding: '5px 8px', paddingLeft: 8 + nivel * 14, cursor: 'pointer',
          background: ativa ? '#E7ECFD' : 'transparent', borderRadius: 4,
          fontSize: nivel === 0 ? 12 : 11,
          fontWeight: ativa ? 600 : (nivel === 0 ? 500 : 400),
          color: ativa ? '#020D7F' : (nivel === 0 ? '#333' : '#555') }}>
          {temFilhos ? (
            <span onClick={(e) => { e.stopPropagation(); toggleColapso(f.id); }}
              style={{ width: 14, textAlign: 'center', color: '#999', userSelect: 'none' }}>
              {aberta ? '▾' : '▸'}
            </span>
          ) : <span style={{ width: 14 }} />}
          <span onClick={() => setPastaAtiva(f.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, overflow: 'hidden' }}>
            <span style={{ fontSize: 14 }}>{nivel === 0 ? (DRIVE_TIPO_ICONE[f.tipo] || '📁') : '📁'}</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap' }}>{f.nome}</span>
            {arq > 0 && <Typography.Text type="secondary" style={{ fontSize: 10 }}>{arq}</Typography.Text>}
          </span>
        </div>
        {temFilhos && aberta && filhos.map(c => renderNo(c, nivel + 1))}
      </div>
    );
  }

  // Coluna da árvore de pastas
  const arvoreRender = (
    <Card size="small" styles={{ body: { padding: 8 } }}>
      {filhosDe(null).map(f => renderNo(f, 0))}
      <Button type="dashed" block icon={<PlusOutlined />}
        onClick={() => { setNovoPastaNome(''); setCriarEm({ parentId: null, parentNome: 'raiz' }); }}
        style={{ marginTop: 6, fontSize: 11, borderColor: '#0930D0', color: '#0930D0' }}>
        Nova pasta
      </Button>
    </Card>
  );

  // Tabela de arquivos
  const colunas: ColumnsType<DriveFile> = [
    { title: 'Nome', key: 'nome', ellipsis: true,
      render: (_, f) => (
        <div>
          <span style={{ fontSize: 14, marginRight: 6 }}>{iconeArquivo(f.tipo_arquivo)}</span>
          <Typography.Link strong onClick={() => abrirArquivo(f)} style={{ color: '#222' }}>
            {f.nome}
          </Typography.Link>
          {f.descricao && (
            <div><Typography.Text type="secondary" style={{ fontSize: 10 }}>{f.descricao}</Typography.Text></div>
          )}
          {f.origem === 'art' && <Tag color="orange" style={{ fontSize: 9, marginLeft: 6 }}>da aba ARTs</Tag>}
          {/* Os dois arquivos têm o MESMO nome ("Documento de venda"), e até
              07/09 quem baixava pelo Drive recebia sempre a via SEM assinatura,
              mesmo com o papel assinado existindo. Agora o Drive entrega a
              assinada — e diz qual é. */}
          {f.origem === 'venda_documento' && (
            f.assinado
              ? <Tag color="green" style={{ fontSize: 9, marginLeft: 6 }}>assinado por todos</Tag>
              : <Tag style={{ fontSize: 9, marginLeft: 6 }}>via sem assinatura</Tag>
          )}
        </div>
      ),
    },
    { title: 'Versão', key: 'versao', width: 80, align: 'center',
      render: (_, f) => (
        <Space size={4}>
          <Typography.Text>v{f.versao}</Typography.Text>
          {f.versao > 1 && (
            <Tooltip title="Ver histórico de versões">
              <Button size="small" type="text" icon={<HistoryOutlined />}
                onClick={() => setVerVersoes(f)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: 'Tamanho', dataIndex: 'tamanho_bytes', key: 'tam', width: 90, align: 'right',
      render: (v) => <Typography.Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{fmtTamanho(v)}</Typography.Text> },
    { title: 'Por', dataIndex: 'criado_por', key: 'por', width: 130, ellipsis: true,
      render: (v?: string) => <Typography.Text style={{ fontSize: 11 }}>{v?.split('@')[0] || '—'}</Typography.Text> },
    { title: 'Em', dataIndex: 'criado_em', key: 'em', width: 100,
      render: (v) => <Typography.Text type="secondary" style={{ fontSize: 11 }}>{fmtData(v)}</Typography.Text> },
    { title: '', key: 'acoes', width: 100, align: 'right',
      render: (_, f) => (
        <Space size={2}>
          <Tooltip title="Abrir / baixar">
            <Button size="small" type="text" icon={<DownloadOutlined />}
              onClick={() => abrirArquivo(f)} />
          </Tooltip>
          {f.origem !== 'art' && (
            <Tooltip title="Excluir">
              <Button size="small" type="text" danger icon={<DeleteOutlined />}
                onClick={() => pedirExcluirArquivo(f)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Descrição da pasta
  const descrPasta = (() => {
    if (!pasta) return null;
    if (pasta.tipo === 'arts') return 'Pasta gerenciada pela aba ARTs (somente-leitura).';
    if (pasta.tipo === 'upload_cliente') return 'Pasta especial pro cliente externo enviar arquivos.';
    if (pasta.tipo === 'comercial') return 'Documento de venda, contrato externo e anexos comerciais.';
    if (pasta.tipo === 'relatorios') return 'Relatórios técnicos entregues.';
    if (pasta.tipo === 'pecas_graficas') return 'Peças gráficas editáveis (DWG, DXF, AI, etc.).';
    if (pasta.tipo === 'usuario') return 'Pasta criada pelo usuário.';
    return null;
  })();

  return (
    <div>
      <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 14 }}>
        <Input.Search allowClear value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar arquivos..." prefix={<SearchOutlined />}
          style={{ flex: 1, minWidth: 200 }} />
        <Button type="primary" icon={<ShareAltOutlined />}
          style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}
          onClick={() => setVerCompartilhar(true)}>
          Compartilhar com cliente
        </Button>
        <Button icon={<TeamOutlined />}
          style={{ borderColor: '#0930D0', color: '#0930D0' }}
          onClick={() => setVerContatos(true)}>
          Contatos externos
        </Button>
      </Flex>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14 }}>
        {arvoreRender}

        <div>
          {pasta ? (
            <>
              <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 10 }}>
                <div>
                  <Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
                    {DRIVE_TIPO_ICONE[pasta.tipo] || '📁'} {pasta.nome}
                  </Typography.Title>
                  {descrPasta && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>{descrPasta}</Typography.Text>
                  )}
                </div>
                <Space size={6}>
                  {podeSubpasta(pasta) && (
                    <Button size="small" icon={<PlusOutlined />}
                      onClick={() => { setNovoPastaNome(''); setCriarEm({ parentId: pasta.id, parentNome: pasta.nome }); }}>
                      Nova subpasta
                    </Button>
                  )}
                  {podeRenomearExcluir(pasta) && (
                    <>
                      <Button size="small" icon={<EditOutlined />} onClick={() => renomearPasta(pasta)}>Renomear</Button>
                      <Popconfirm title={`Excluir a pasta "${pasta.nome}"? Precisa estar vazia.`}
                        okText="Excluir" okButtonProps={{ danger: true }}
                        onConfirm={() => excluirPasta(pasta)}>
                        <Button size="small" danger icon={<DeleteOutlined />}>Excluir pasta</Button>
                      </Popconfirm>
                    </>
                  )}
                </Space>
              </Flex>

              {/* Dropzone */}
              {podeUpload && (
                <div onDragOver={e => { e.preventDefault(); setDragSobre(true); }}
                  onDragLeave={() => setDragSobre(false)}
                  onDrop={e => {
                    e.preventDefault(); setDragSobre(false);
                    if (e.dataTransfer.files?.length) uploadArquivos(e.dataTransfer.files);
                  }}
                  style={{ border: dragSobre ? '2px dashed #0930D0' : '1px dashed #ccc',
                    borderRadius: 6, padding: 14, textAlign: 'center', marginBottom: 12,
                    background: dragSobre ? '#E7ECFD' : '#FAFAFA' }}>
                  {enviando ? (
                    <div style={{ textAlign: 'left' }}>
                      <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                        <Typography.Text strong style={{ color: '#0930D0', fontSize: 12 }}>
                          Enviando {enviando.atual} de {enviando.total}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 11, maxWidth: '60%',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {enviando.nome}
                        </Typography.Text>
                      </Flex>
                      <Progress percent={enviando.pct} size="small" status="active"
                        strokeColor="#0930D0" />
                    </div>
                  ) : (
                    <Space direction="vertical" size={6}>
                      <Typography.Text>
                        <CloudUploadOutlined /> Arraste arquivos aqui pra enviar para <strong>{pasta.nome}</strong>
                      </Typography.Text>
                      <input type="file" multiple
                        onChange={e => { if (e.target.files) uploadArquivos(e.target.files); e.target.value = ''; }}
                        style={{ fontSize: 10 }} />
                    </Space>
                  )}
                </div>
              )}

              {arquivosFiltrados.length === 0 ? (
                <Empty description={busca ? 'Nenhum arquivo encontrado pro filtro.' : 'Pasta vazia.'} />
              ) : (
                <Table<DriveFile> size="small" rowKey="id" columns={colunas}
                  dataSource={arquivosFiltrados} pagination={false} />
              )}
            </>
          ) : (
            <Empty description="Selecione uma pasta na árvore à esquerda." />
          )}
        </div>
      </div>

      <Modal open={!!criarEm} onCancel={() => { setCriarEm(null); setNovoPastaNome(''); }}
        onOk={criarPasta} okText="Criar"
        okButtonProps={{ disabled: !novoPastaNome.trim() }}
        title={criarEm?.parentId ? `Nova subpasta em "${criarEm.parentNome}"` : 'Nova pasta'}
        destroyOnClose width={420}>
        <Input autoFocus value={novoPastaNome} placeholder="Nome da pasta"
          onChange={e => setNovoPastaNome(e.target.value)} onPressEnter={criarPasta} />
      </Modal>

      {verVersoes && (
        <ModalVersoesDrive arquivo={verVersoes}
          onClose={() => setVerVersoes(null)}
          onAtualizar={() => { setVerVersoes(null); recarregar(); }} />
      )}

      {verContatos && (
        <ModalContatosDrive contrato={contrato} onClose={() => setVerContatos(false)} />
      )}

      {verCompartilhar && (
        <ModalCompartilharDrive contrato={contrato} onClose={() => setVerCompartilhar(false)} />
      )}
    </div>
  );
}

// ════════════════ MODAL: HISTÓRICO DE VERSÕES ════════════════

function ModalVersoesDrive({ arquivo, onClose, onAtualizar }: {
  arquivo: DriveFile; onClose: () => void; onAtualizar: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [versoes, setVersoes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_drive_listar_versoes', documento_id: arquivo.id },
          { headers: { Authorization: token } });
        const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
        setVersoes(Array.isArray(d) ? d : []);
      } finally { setLoading(false); }
    })();
  }, [arquivo.id]);

  async function baixarVersao(v: any) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_download_url', documento_id: v.id },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      if (d.url) window.open(d.url, '_blank');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  function pedirRestaurar(v: any) {
    modal.confirm({
      title: `Restaurar v${v.versao} como versão atual?`,
      content: 'A versão atual passa a ficar no histórico.',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_drive_restaurar_versao', documento_id: v.id },
            { headers: { Authorization: token } });
          message.success('Versão restaurada.');
          onAtualizar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  const colunas = [
    { title: 'Versão', dataIndex: 'versao', key: 'v',
      render: (v: number, row: any) => (
        <Space size={4}>
          <Typography.Text>v{v}</Typography.Text>
          {row.vigente && <Tag color="green" style={{ fontSize: 9 }}>ATUAL</Tag>}
        </Space>
      ),
    },
    { title: 'Tamanho', dataIndex: 'tamanho_bytes', key: 't', align: 'right' as const,
      render: (v: number) => <Typography.Text style={{ fontFamily: 'monospace' }}>{fmtTamanho(v)}</Typography.Text> },
    { title: 'Por', dataIndex: 'criado_por', key: 'por',
      render: (v?: string) => v?.split('@')[0] || '—' },
    { title: 'Em', dataIndex: 'criado_em', key: 'em',
      render: (v: string) => <Typography.Text type="secondary">{fmtData(v)}</Typography.Text> },
    { title: '', key: 'acoes', align: 'right' as const,
      render: (_: any, v: any) => (
        <Space size={4}>
          <Tooltip title="Baixar">
            <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => baixarVersao(v)} />
          </Tooltip>
          {!v.vigente && (
            <Button size="small" onClick={() => pedirRestaurar(v)}
              style={{ borderColor: '#0a7f4a', color: '#0a7f4a' }}>
              Restaurar
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal open onCancel={onClose} width={640} footer={null}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        <HistoryOutlined /> Versões de "{arquivo.nome}"
      </Typography.Title>}>
      {loading ? <Flex justify="center" style={{ padding: 30 }}><Spin /></Flex>
        : <Table size="small" rowKey="id" columns={colunas} dataSource={versoes} pagination={false} />}
    </Modal>
  );
}

// ════════════════ MODAL: CONTATOS EXTERNOS DO DRIVE ════════════════

function ModalContatosDrive({ contrato, onClose }: {
  contrato: Contrato; onClose: () => void;
}) {
  const { message } = AntApp.useApp();
  const [contatos, setContatos] = useState<any[]>([]);
  const [novoEmail, setNovoEmail] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function recarregar() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_listar_contatos', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setContatos(Array.isArray(d) ? d : []);
    } catch {}
  }
  useEffect(() => { recarregar(); /* eslint-disable-next-line */ }, []);

  async function adicionar() {
    if (!novoEmail.trim()) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_adicionar_contato',
          contrato_id: contrato.id, email: novoEmail.trim(), nome: novoNome.trim() || null },
        { headers: { Authorization: token } });
      setNovoEmail(''); setNovoNome('');
      recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function remover(c: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_remover_contato', id: c.id },
        { headers: { Authorization: token } });
      recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function toggleNotificar(c: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_atualizar_contato', id: c.id, notificar_uploads: !c.notificar_uploads },
        { headers: { Authorization: token } });
      recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  return (
    <Modal open onCancel={onClose} width={620} footer={null}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        <TeamOutlined /> Contatos externos do Drive
      </Typography.Title>}>
      <Alert type="info" message="Estes contatos receberão e-mails automáticos quando arquivos forem adicionados/atualizados no Drive deste contrato."
        style={{ marginBottom: 14 }} />

      {contatos.length === 0 ? (
        <Empty description="Nenhum contato cadastrado." style={{ marginBottom: 14 }} />
      ) : (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 14 }} size={6}>
          {contatos.map(c => (
            <Card key={c.id} size="small" styles={{ body: { padding: 8 } }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto',
                gap: 10, alignItems: 'center' }}>
                <div>
                  <Typography.Text strong>{c.nome || c.email.split('@')[0]}</Typography.Text>
                  <div><Typography.Text type="secondary" style={{ fontSize: 11 }}>{c.email}</Typography.Text></div>
                  {c.contato_nome_db && (
                    <Typography.Text style={{ fontSize: 10, color: '#0a7f4a' }}>
                      ✓ vinculado a {c.contato_nome_db} (cadastrado)
                    </Typography.Text>
                  )}
                </div>
                <Checkbox checked={!!c.notificar_uploads}
                  onChange={() => toggleNotificar(c)}>
                  <Typography.Text style={{ fontSize: 10 }}>Notificar</Typography.Text>
                </Checkbox>
                <Popconfirm title={`Remover ${c.email}?`} onConfirm={() => remover(c)}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </Card>
          ))}
        </Space>
      )}

      <Card size="small" title={<Typography.Text strong style={{ color: '#020D7F' }}>
        + Adicionar contato
      </Typography.Text>}>
        <Form layout="vertical" disabled={salvando}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <Input value={novoNome} onChange={e => setNovoNome(e.target.value)} placeholder="Nome (opcional)" />
            <Input value={novoEmail} onChange={e => setNovoEmail(e.target.value)} placeholder="email@cliente.com" />
          </div>
          <Button type="primary" icon={<PlusOutlined />} loading={salvando}
            disabled={!novoEmail.trim()} onClick={adicionar}>
            Adicionar
          </Button>
        </Form>
      </Card>
    </Modal>
  );
}

// ════════════════ MODAL: COMPARTILHAR DRIVE COM CLIENTE ════════════════

function ModalCompartilharDrive({ contrato, onClose }: {
  contrato: Contrato; onClose: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [permitirUpload, setPermitirUpload] = useState(true);
  const [expiraEm, setExpiraEm] = useState('');
  const [copiado, setCopiado] = useState(false);

  async function recarregar() {
    setLoading(true);
    try {
      const t = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_obter_token', contrato_id: contrato.id },
        { headers: { Authorization: t } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setTokenInfo(d || null);
      if (d) {
        setPermitirUpload(!!d.permissao_upload);
        setExpiraEm(d.expira_em ? String(d.expira_em).slice(0, 10) : '');
      }
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { recarregar(); /* eslint-disable-next-line */ }, [contrato.id]);

  async function gerarOuSalvar() {
    setSalvando(true);
    try {
      const t = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_drive_gerar_token',
          contrato_id: contrato.id,
          permissao_upload: permitirUpload,
          expira_em: expiraEm || null },
        { headers: { Authorization: t } });
      message.success('Configurações salvas.');
      await recarregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  function pedirRevogar() {
    modal.confirm({
      title: 'Revogar o link público?',
      content: 'Quem tiver o link perde acesso imediatamente.',
      okText: 'Revogar', okButtonProps: { danger: true },
      onOk: async () => {
        setSalvando(true);
        try {
          const t = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_drive_revogar_token', contrato_id: contrato.id },
            { headers: { Authorization: t } });
          message.success('Acesso revogado.');
          setTokenInfo(null);
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        } finally { setSalvando(false); }
      },
    });
  }

  function setExpiraAtalho(dias: number | null) {
    if (dias === null) { setExpiraEm(''); return; }
    const d = new Date();
    d.setDate(d.getDate() + dias);
    setExpiraEm(d.toISOString().slice(0, 10));
  }

  const linkPublico = tokenInfo
    ? `${window.location.origin}/#/drive/${tokenInfo.hash}`
    : '';

  function copiarLink() {
    if (!linkPublico) return;
    navigator.clipboard.writeText(linkPublico).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    });
  }

  const expirado = tokenInfo?.expira_em && new Date(tokenInfo.expira_em) < new Date();

  return (
    <Modal open onCancel={onClose} width={620} footer={null}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        <ShareAltOutlined /> Compartilhar Drive com cliente
      </Typography.Title>}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 11 }}>
        Gera um link público (sem login) que mostra o Drive deste contrato.
        Cliente baixa qualquer arquivo das pastas gerais e pode enviar arquivos
        na pasta <strong>Upload do Cliente</strong>.
      </Typography.Paragraph>

      {loading ? (
        <Flex justify="center" style={{ padding: 30 }}><Spin /></Flex>
      ) : (
        <>
          {tokenInfo ? (
            <Card size="small" style={{ marginBottom: 12, background: '#f4f6fb' }}>
              <Typography.Text strong style={{ fontSize: 10, color: '#555',
                textTransform: 'uppercase', letterSpacing: 0.5 }}>Link público</Typography.Text>
              <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                <Input readOnly value={linkPublico} style={{ fontFamily: 'monospace' }} />
                <Button type={copiado ? 'primary' : 'default'}
                  style={copiado ? { background: '#0a7f4a', borderColor: '#0a7f4a' } : undefined}
                  icon={copiado ? <CheckOutlined /> : <CopyOutlined />} onClick={copiarLink}>
                  {copiado ? 'Copiado' : 'Copiar'}
                </Button>
              </Space.Compact>
              {expirado && (
                <Alert type="error" style={{ marginTop: 8 }}
                  message={`Este link expirou em ${fmtData(tokenInfo.expira_em)}. Atualize a expiração ou revogue.`} />
              )}
            </Card>
          ) : (
            <Alert type="info" message='Nenhum link público ativo. Configure abaixo e clique em "Gerar link".'
              style={{ marginBottom: 12 }} />
          )}

          <Checkbox checked={permitirUpload}
            onChange={e => setPermitirUpload(e.target.checked)}>
            <Typography.Text strong>Permitir upload pelo cliente</Typography.Text>
            <div><Typography.Text type="secondary" style={{ fontSize: 10 }}>
              Cliente pode enviar arquivos na pasta "Upload do Cliente". Demais pastas: somente leitura.
            </Typography.Text></div>
          </Checkbox>

          <div style={{ marginTop: 14, marginBottom: 14 }}>
            <Typography.Text strong style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 6 }}>
              Expiração do link
            </Typography.Text>
            <Space size={6} wrap style={{ marginBottom: 8 }}>
              <Button size="small" onClick={() => setExpiraAtalho(1)}>24h</Button>
              <Button size="small" onClick={() => setExpiraAtalho(7)}>7 dias</Button>
              <Button size="small" onClick={() => setExpiraAtalho(30)}>30 dias</Button>
              <Button size="small" onClick={() => setExpiraAtalho(90)}>90 dias</Button>
              <Button size="small" type={!expiraEm ? 'primary' : 'default'}
                onClick={() => setExpiraAtalho(null)}>Nunca</Button>
            </Space>
            <DatePicker value={expiraEm ? dayjs(expiraEm) : null}
              onChange={(d) => setExpiraEm(d ? d.format('YYYY-MM-DD') : '')}
              format="DD/MM/YYYY" style={{ width: 200 }} />
            <div><Typography.Text type="secondary" style={{ fontSize: 10 }}>
              {expiraEm ? `Expira em ${fmtData(expiraEm)}` : 'Sem expiração'}
            </Typography.Text></div>
          </div>

          <Flex gap={8} wrap>
            <Button type="primary" loading={salvando} onClick={gerarOuSalvar}
              style={{ flex: 1, minWidth: 200 }} icon={<ShareAltOutlined />}>
              {tokenInfo ? 'Atualizar configurações' : 'Gerar link'}
            </Button>
            {tokenInfo && (
              <Button danger onClick={pedirRevogar} disabled={salvando}>
                Revogar acesso
              </Button>
            )}
          </Flex>

          {tokenInfo && (
            <Alert type="warning" style={{ marginTop: 14 }}
              message={<Typography.Text style={{ fontSize: 11 }}>
                <strong>E-mail automático:</strong> os contatos cadastrados em "Contatos externos"
                com "Notificar" recebem e-mail toda vez que um arquivo é adicionado/atualizado.
              </Typography.Text>} />
          )}
        </>
      )}
    </Modal>
  );
}
